/**
 * Edição manual inline de um trecho do laudo (E23.F20 — ADR-0030). // ux-neutrality:allow doc-comment
 *
 * Complementa o editor conversacional: o perito edita o texto de um campo à // ux-neutrality:allow doc-comment
 * mão e confirma com `source='manual'`, gravando na mesma trilha auditável // ux-neutrality:allow doc-comment
 * (`laudo_edits`) da E23.F8. Cobre os campos de prosa; listas seguem pelo // ux-neutrality:allow doc-comment
 * fluxo conversacional. // ux-neutrality:allow doc-comment
 */
import { useCallback, useState } from 'react';
import { Pencil } from 'lucide-react';
import { Button, Textarea } from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import { useApiClient } from '@/lib/api';
import { LaudoError } from '@/lib/laudo';
import {
  confirmLaudoEdit,
  type LaudoPreviewContestadaData,
  type ProposableField,
} from '@/lib/laudo_edits';
import { narrativeFieldLabel } from '@/lib/laudo_fields';

// Campos de prosa (string única) editáveis à mão. Coincide com o conjunto que
// o Agente também propõe (ProposableField).
const MANUAL_FIELDS: readonly ProposableField[] = [
  'estrutura_geral',
  'morfologia',
  'conectividade',
  'inclinacao',
  'sequencia',
  'avaliacao_tecnica',
  'sintese',
  'observacao_adicional',
] as const;

interface LaudoManualFieldEditorProps {
  caseId: string;
  contestada: LaudoPreviewContestadaData;
  onEdited: () => void;
}

export function LaudoManualFieldEditor({
  caseId,
  contestada,
  onEdited,
}: LaudoManualFieldEditorProps): JSX.Element {
  const t = ptBR.laudoCaso.manualEdit;
  const client = useApiClient();
  const [editingField, setEditingField] = useState<ProposableField | null>(null);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const startEdit = useCallback(
    (field: ProposableField) => {
      setEditingField(field);
      setDraft((contestada[field] as string | null) ?? '');
      setError(null);
    },
    [contestada],
  );

  const cancel = useCallback(() => {
    setEditingField(null);
    setError(null);
  }, []);

  const save = useCallback(async () => {
    if (!editingField) return;
    setSaving(true);
    setError(null);
    try {
      await confirmLaudoEdit(client, caseId, {
        caseAnalysisId: contestada.case_analysis_id,
        targetField: editingField,
        newValue: draft,
        source: 'manual',
      });
      setEditingField(null);
      onEdited();
    } catch (err) {
      const reason = err instanceof LaudoError ? err.reason : 'unknown';
      setError(reason === 'narrative_already_approved' ? t.saveErrorApproved : t.saveError);
    } finally {
      setSaving(false);
    }
  }, [client, caseId, contestada.case_analysis_id, editingField, draft, onEdited, t]);

  return (
    <div
      className="mt-sc-4 border-t border-border pt-sc-3"
      data-testid={`laudo-manual-edit-${contestada.letra}`}
    >
      <h4 className="text-caption font-semibold text-fg">{t.sectionTitle}</h4>
      <p className="mt-0.5 text-caption text-fg-muted">{t.sectionHint}</p>
      <ul className="mt-sc-2 flex flex-col gap-sc-2">
        {MANUAL_FIELDS.map((field) => {
          const value = (contestada[field] as string | null) ?? '';
          const label = narrativeFieldLabel(field);
          const isEditing = editingField === field;
          return (
            <li key={field}>
              <div className="flex items-center justify-between gap-2">
                <span className="text-caption font-medium text-fg-muted">{label}</span>
                {!isEditing && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => startEdit(field)}
                    aria-label={`${t.editCta}: ${label}`}
                    className="h-7 shrink-0 px-1.5 text-fg-muted"
                    data-testid={`laudo-manual-edit-btn-${contestada.letra}-${field}`}
                  >
                    <Pencil className="h-3.5 w-3.5" aria-hidden="true" strokeWidth={1.5} />
                  </Button>
                )}
              </div>
              {isEditing ? (
                <div className="mt-1">
                  <Textarea
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    rows={3}
                    data-testid={`laudo-manual-edit-input-${contestada.letra}-${field}`}
                  />
                  {error && (
                    <p role="alert" className="mt-1 text-caption text-danger-700">
                      {error}
                    </p>
                  )}
                  <div className="mt-1 flex gap-sc-2">
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      onClick={() => void save()}
                      disabled={saving}
                      data-testid={`laudo-manual-edit-save-${contestada.letra}-${field}`}
                    >
                      {saving ? t.saving : t.saveCta}
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={cancel}
                      data-testid={`laudo-manual-edit-cancel-${contestada.letra}-${field}`}
                    >
                      {t.cancelCta}
                    </Button>
                  </div>
                </div>
              ) : (
                <p className="truncate text-body-sm text-fg" title={value || undefined}>
                  {value || t.emptyValue}
                </p>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
